import { MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_LABEL } from '../../services/file.service';

/**
 * The upload dialog's rules (Ivan 2026-11-03: paste an image as in Slack or Claude, and the place form's 20 MB, not
 * "Max 500MB"): the size it says, the types it takes from a drop or a paste (the picker's `accept` only filters the
 * picker), and a name for a pasted file, which has none of its own.
 */

/** "20 MB" for the caller's cap; the app-wide default keeps its own label. */
export function sizeLabel(maxBytes: number | undefined): string {
  if (!maxBytes || maxBytes === MAX_FILE_SIZE_BYTES) return MAX_FILE_SIZE_LABEL;
  const mb = maxBytes / (1024 * 1024);
  return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`;
}

const tokens = (accept: string | undefined) => (accept ?? '*').split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);

/** The server's cap on a file fetched from a link, whatever the caller's (server api/classes/file-from-link.ts). */
export const LINK_MAX_BYTES = 25 * 1024 * 1024;

/** " (25 MB from a link)" when the caller's cap is above what a link can fetch. */
export function linkCapNote(maxBytes: number | undefined, noLink: boolean | undefined): string {
  return !noLink && (maxBytes ?? MAX_FILE_SIZE_BYTES) > LINK_MAX_BYTES ? ` (${sizeLabel(LINK_MAX_BYTES)} from a link)` : '';
}

/** A type by a file's extension, for a file the browser gives none (no MIME mapping for it on that machine). */
const TYPE_BY_EXT: Record<string, string> = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', avif: 'image/avif', gif: 'image/gif', svg: 'image/svg+xml',
  pdf: 'application/pdf', doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  mp4: 'video/mp4',
};

/** Whether `accept` ("image/jpeg,image/png", "image/*", ".pdf", "*") takes this file. */
export function accepts(accept: string | undefined, file: { name?: string | null; type?: string | null }): boolean {
  const list = tokens(accept);
  if (!list.length || list.includes('*') || list.includes('*/*')) return true;
  const name = (file.name ?? '').toLowerCase();
  const given = (file.type ?? '').toLowerCase();
  const type = given === 'image/jpg' ? 'image/jpeg' : given || (TYPE_BY_EXT[name.split('.').pop() ?? ''] ?? '');
  return list.some((t) => (t.startsWith('.') ? name.endsWith(t) : t.endsWith('/*') ? type.startsWith(t.slice(0, -1)) : type === t));
}

const LABEL: Record<string, string> = {
  'image/jpeg': 'JPG', 'image/png': 'PNG', 'image/webp': 'WebP', 'image/avif': 'AVIF', 'image/gif': 'GIF', 'image/svg+xml': 'SVG',
  'application/pdf': 'PDF', '.pdf': 'PDF', 'image/*': 'images', 'video/*': 'videos', 'video/mp4': 'MP4',
  'application/msword': 'Word', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word', '.doc': 'Word', '.docx': 'Word',
};

/** A type this doesn't know, readable: ".xlsx" and "text/csv" as "XLSX" and "CSV", never a long MIME string. */
function unknownLabel(t: string): string {
  if (t.startsWith('.')) return t.slice(1).toUpperCase();
  if (t.endsWith('/*')) return `${t.slice(0, -2)} files`;
  const sub = (t.split('/')[1] ?? '').split('+')[0].replace(/^(vnd\.|x-)/, '');
  return /^[a-z0-9]{1,6}$/.test(sub) ? sub.toUpperCase() : 'other files';
}

/** "JPG, PNG or WebP" (null when it takes anything). */
export function acceptLabel(accept: string | undefined): string | null {
  const list = tokens(accept);
  if (!list.length || list.includes('*') || list.includes('*/*')) return null;
  const names = [...new Set(list.map((t) => LABEL[t] ?? unknownLabel(t)))].sort((a, b) => +(a === 'other files') - +(b === 'other files'));
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}` : names[0];
}

/** A queued file of a type the dialog doesn't take: "This takes only JPG, PNG or WebP" (no article to get wrong). */
export function typeError(typesLabel: string | null): string {
  return typesLabel ? `This takes only ${typesLabel}` : 'This file type isn\'t accepted';
}

/** The paste shortcut on this platform ("⌘V" on a Mac, iPad or iPhone, "Ctrl+V" elsewhere). */
export function pasteKeys(platform: string | null | undefined): string {
  return /mac|iphone|ipad|ipod/i.test(platform ?? '') ? '⌘V' : 'Ctrl+V';
}

/** A pasted file keeps its own name unless it's the browser's generic one ("image.png") or has none. */
export function hasOwnName(name: string | null | undefined): boolean {
  return !!name && !/^image\.(png|jpe?g|gif|webp)$/i.test(name);
}

const EXT: Record<string, string> = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/gif': 'gif', 'application/pdf': 'pdf' };

/** A pasted file's name: "St. Nicolas' Anglican Church 1.png", clear of characters a file name can't carry. */
export function pastedFileName(base: string | null | undefined, n: number, type: string): string {
  const clean = (base ?? '').replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80);
  const ext = EXT[type.toLowerCase()] ?? (type.split('/')[1] || 'bin').replace(/[^a-z0-9]/gi, '').slice(0, 5);
  return `${clean || (type.startsWith('image/') ? 'Pasted image' : 'Pasted file')} ${n}.${ext}`;
}

/** The files on a clipboard or a drop (some browsers fill `items` only). */
export function filesOf(data: DataTransfer | null | undefined): File[] {
  if (!data) return [];
  if (data.files?.length) return Array.from(data.files);
  return Array.from(data.items ?? [])
    .filter((i) => i.kind === 'file')
    .map((i) => i.getAsFile())
    .filter((f): f is File => !!f);
}

/** A paste that is a link and nothing else ("From a link", 2026-11-03). */
export function isLinkText(text: string | null | undefined): boolean {
  return /^https?:\/\/\S+$/i.test((text ?? '').trim());
}

/** Whether a web page's images can be offered: the dialog takes images. */
export function takesImages(accept: string | undefined): boolean {
  return ['a.jpg', 'a.png', 'a.webp'].some((name) => accepts(accept, { name, type: `image/${name.slice(2).replace('jpg', 'jpeg')}` }));
}

/** The link box's hint: a page's images only where the dialog takes images. */
export function linkPlaceholder(accept: string | undefined): string {
  return takesImages(accept) ? 'Or paste a link: the file\'s own, or a web page with an image' : 'Or paste the file\'s own link';
}
